import * as React from 'react';
import {
    Checkbox,
    DefaultButton,
    PrimaryButton,
    Stack,
    Text,
    TextField,
} from '@fluentui/react';
import type {
    AgentTaskQuestion,
    AgentTaskQuestionContext,
    AgentTaskQuestionResolution,
    AgentTaskQuestionSourceLocation,
} from '../types';
import { formatDate } from '../helpers';

export interface AgentTaskQuestionDraft {
    answer: string;
    declined: boolean;
}

export interface AgentTaskQuestionCollectionEditor {
    drafts: Record<number, AgentTaskQuestionDraft>;
    changedQuestionIds: number[];
    unansweredQuestionCount: number;
    updateDraft: (question: AgentTaskQuestion, patch: Partial<AgentTaskQuestionDraft>) => void;
    declineUnanswered: () => void;
    getDraft: (question: AgentTaskQuestion) => AgentTaskQuestionDraft;
    getChangedResolutions: () => AgentTaskQuestionResolution[];
    resetDrafts: () => void;
}

function formatLocationLines(location: AgentTaskQuestionSourceLocation) {
    if (location.startLine === undefined) return undefined;
    return location.endLine !== undefined && location.endLine !== location.startLine
        ? `Lines ${location.startLine}–${location.endLine}`
        : `Line ${location.startLine}`;
}

export function AgentTaskQuestionContextView({ context }: { context: AgentTaskQuestionContext }) {
    return (
        <Stack
            tokens={{ childrenGap: 8 }}
            styles={{ root: { margin: '8px 0 12px', padding: 12, background: '#f3f2f1', borderRadius: 4 } }}
        >
            {context.candidateChange && (
                <Text>
                    <strong>Candidate change:</strong>{' '}
                    {context.candidateChange.summary || 'Unspecified change'}
                    {context.candidateChange.id ? ` (${context.candidateChange.id})` : ''}
                </Text>
            )}
            {context.decisionImpact && (
                <Text><strong>Decision impact:</strong> {context.decisionImpact}</Text>
            )}
            {context.locations.map((location, index) => {
                const lineRange = formatLocationLines(location);
                const labelParts = [
                    location.repository,
                    location.project,
                    location.path,
                    location.symbol,
                    lineRange,
                ].filter(Boolean);
                const locationLabel = labelParts.join(' • ') || `Source location ${index + 1}`;
                return (
                    <div key={`${location.repository ?? ''}:${location.path ?? ''}:${location.startLine ?? ''}:${index}`}>
                        {location.codeUrl ? (
                            <a href={location.codeUrl} target="_blank" rel="noopener noreferrer">
                                {locationLabel}
                            </a>
                        ) : (
                            <Text>{locationLabel}</Text>
                        )}
                        {location.ref && <div><Text variant="small">Ref: {location.ref}</Text></div>}
                        {location.snippet && (
                            <details style={{ marginTop: 4 }}>
                                <summary style={{ cursor: 'pointer' }}>Show code snippet</summary>
                                <pre style={{ margin: '6px 0 0', padding: 8, overflowX: 'auto', background: '#ffffff' }}>
                                    <code>{location.snippet}</code>
                                </pre>
                            </details>
                        )}
                    </div>
                );
            })}
        </Stack>
    );
}

export function useAgentTaskQuestionCollectionEditor(
    questions: AgentTaskQuestion[],
): AgentTaskQuestionCollectionEditor {
    const [drafts, setDrafts] = React.useState<Record<number, AgentTaskQuestionDraft>>({});
    const collectionKey = questions
        .map(question => [
            question.questionId,
            question.rowVersion ?? '',
            question.answer ?? '',
            question.declined ? 1 : 0,
        ].join(':'))
        .join('|');

    React.useEffect(() => {
        setDrafts({});
    }, [collectionKey]);

    const getDraft = React.useCallback((question: AgentTaskQuestion): AgentTaskQuestionDraft => (
        drafts[question.questionId] ?? {
            answer: question.answer ?? '',
            declined: question.declined,
        }
    ), [drafts]);

    const updateDraft = React.useCallback((
        question: AgentTaskQuestion,
        patch: Partial<AgentTaskQuestionDraft>,
    ) => {
        setDrafts(current => ({
            ...current,
            [question.questionId]: {
                answer: current[question.questionId]?.answer ?? question.answer ?? '',
                declined: current[question.questionId]?.declined ?? question.declined,
                ...patch,
            },
        }));
    }, []);

    const declineUnanswered = React.useCallback(() => {
        setDrafts(current => {
            let next = current;
            questions.forEach(question => {
                const draft = current[question.questionId];
                const answer = draft?.answer ?? question.answer ?? '';
                const declined = draft?.declined ?? question.declined;
                if (!declined && !answer.trim()) {
                    if (next === current) next = { ...current };
                    next[question.questionId] = { answer, declined: true };
                }
            });
            return next;
        });
    }, [questions]);

    const changedQuestionIds = React.useMemo(
        () => Object.keys(drafts).map(Number),
        [drafts],
    );
    const unansweredQuestionCount = React.useMemo(() => questions.filter(question => {
        const draft = drafts[question.questionId];
        const answer = draft?.answer ?? question.answer ?? '';
        const declined = draft?.declined ?? question.declined;
        return !declined && !answer.trim();
    }).length, [drafts, questions]);

    const getChangedResolutions = React.useCallback(() => changedQuestionIds.map(questionId => ({
        questionId,
        answer: drafts[questionId].declined ? null : drafts[questionId].answer.trim(),
        declined: drafts[questionId].declined,
        rowVersion: questions.find(question => question.questionId === questionId)?.rowVersion,
    })), [changedQuestionIds, drafts, questions]);
    const resetDrafts = React.useCallback(() => setDrafts({}), []);

    return {
        drafts,
        changedQuestionIds,
        unansweredQuestionCount,
        updateDraft,
        declineUnanswered,
        getDraft,
        getChangedResolutions,
        resetDrafts,
    };
}

export function AgentTaskQuestionEditor({
    question,
    draft,
    readOnly,
    onChange,
}: {
    question: AgentTaskQuestion;
    draft: AgentTaskQuestionDraft;
    readOnly: boolean;
    onChange?: (patch: Partial<AgentTaskQuestionDraft>) => void;
}) {
    return (
        <div style={{ borderBottom: '1px solid #edebe9', paddingBottom: 12 }}>
            <Text variant="mediumPlus">{question.question}</Text>
            {question.context && <AgentTaskQuestionContextView context={question.context} />}
            <div style={{ color: '#605e5c', margin: '4px 0 8px' }}>
                Question #{question.questionId}
                {question.jobName ? ` • ${question.jobName}` : ''}
                {question.workItemId ? ` • Work item #${question.workItemId}` : ''}
                {question.createdDate ? ` • ${formatDate(question.createdDate)}` : ''}
            </div>
            <TextField
                label="Answer"
                multiline
                rows={3}
                value={draft.answer}
                disabled={readOnly || draft.declined}
                onChange={(_, value) => onChange?.({ answer: value ?? '' })}
            />
            <Checkbox
                label="Decline to answer"
                checked={draft.declined}
                disabled={readOnly}
                onChange={(_, checked) => onChange?.({ declined: !!checked })}
            />
            {readOnly && (
                <Text variant="small">
                    {question.declined ? 'Declined' : 'Answered'}
                    {question.resolvedDate ? ` • ${formatDate(question.resolvedDate)}` : ''}
                </Text>
            )}
        </div>
    );
}

export function AgentTaskQuestionCollectionActions({
    editor,
    saving,
    onSubmit,
}: {
    editor: AgentTaskQuestionCollectionEditor;
    saving: boolean;
    onSubmit: () => void;
}) {
    return (
        <Stack horizontal tokens={{ childrenGap: 8 }}>
            <DefaultButton
                text="Decline unanswered"
                disabled={saving || editor.unansweredQuestionCount === 0}
                onClick={editor.declineUnanswered}
            />
            <PrimaryButton
                text={`Submit changed (${editor.changedQuestionIds.length})`}
                disabled={saving || editor.changedQuestionIds.length === 0}
                onClick={onSubmit}
            />
        </Stack>
    );
}
